<template>
  <div :style="backgroundStyle" class="carousel-container">
    <div class="carousel-header">
      <div class="heading">{{ immersiveProductData.title }}</div>
      <div class="sub-title">{{ immersiveProductData.subtitle }}</div>
    </div>
    <div class="immersive-carousel">
      <button
        class="nav-arrow left-arrow"
        :aria-label="ariaLabelPrev"
        title="Carousel button navigation prev"
        @click="prev"
      >
        <svg class="icon">
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#arrow-back"></use>
        </svg>
      </button>
      <div ref="carouselTrack" class="carousel-track">
        <div
          v-for="(product, index) in immersiveProductData.products"
          :key="index"
          ref="carouselItems"
          class="carousel-item"
          :class="getClass(index)"
          @click="redirectToProduct(product.url)"
        >
          <img :src="product.productFirstImage.Src" :alt="product.title" />
          <transition-group name="fade" mode="out-in">
            <span
              v-if="isCenter(index)"
              :key="`product-type-${index}`"
              class="product-type"
              :class="{ 'is-visible': !isTransitioning }"
              >{{ product.producttype }}</span
            >
            <div
              v-if="isCenter(index)"
              :key="`product-info-${index}`"
              class="product-info"
              :class="{ 'is-visible': !isTransitioning }"
            >
              <div ref="productTitle" class="product-title">{{ product.title }}</div>
              <p ref="productDesc" class="product-desc">{{ product.summary }}</p>
              <button class="product-cta" @click="onButtonClick(index)">
                {{ immersiveProductData.cta_label }}
              </button>
            </div>
          </transition-group>
        </div>
      </div>
      <button
        class="nav-arrow right-arrow"
        :aria-label="ariaLabelNext"
        title="Carousel button navigation next"
        @click="next"
      >
        <svg class="icon">
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#arrow-icon"></use>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'immersive-carousel',
  props: {
    immersiveProductData: {
      type: Object,
      default: null,
    },
    ariaLabelPrev: {
      type: String,
      default: '',
    },
    ariaLabelNext: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentIndex: 0,
      isTransitioning: false,
    };
  },
  computed: {
    backgroundStyle() {
      if (this.immersiveProductData.background_image) {
        return {
          backgroundImage: `url('${this.immersiveProductData.background_image}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      } else {
        return {
          backgroundColor: this.immersiveProductData.hex_code,
        };
      }
    },
  },
  mounted() {
    setTimeout(() => {
      this.adjustDescLines();
      this.truncateTitles();
    }, 500);
  },
  methods: {
    next() {
      if (this.isTransitioning) return;
      this.isTransitioning = true;

      this.currentIndex = (this.currentIndex + 1) % this.immersiveProductData.products.length;
      this.updateClasses();

      setTimeout(() => {
        this.isTransitioning = false;
        this.$nextTick(() => {
          this.adjustDescLines();
          this.truncateTitles();
        });
      }, 200);
    },
    prev() {
      if (this.isTransitioning) return;
      this.isTransitioning = true;

      this.currentIndex =
        (this.currentIndex - 1 + this.immersiveProductData.products.length) %
        this.immersiveProductData.products.length;
      this.updateClasses();
      setTimeout(() => {
        this.isTransitioning = false;
        this.$nextTick(() => {
          this.adjustDescLines();
          this.truncateTitles();
        });
      }, 200);
    },
    updateClasses() {
      const carouselItems = this.$refs.carouselItems;
      carouselItems.forEach((item, index) => {
        item.className = 'carousel-item ' + this.getClass(index);
      });
    },
    adjustDescLines() {
      const carouselItems = document.querySelectorAll('.carousel-item.is-center');

      carouselItems.forEach((item) => {
        const title = item.querySelector('.product-title');
        const desc = item.querySelector('.product-desc');

        if (title && desc) {
          const lineHeight = parseInt(getComputedStyle(desc).lineHeight);
          if (isNaN(lineHeight)) return;
          const maxLines = title.clientHeight > lineHeight * 1.2 ? 2 : 3;
          this.truncateText(desc, maxLines);
        }
      });
    },
    truncateTitles() {
      const carouselItems = document.querySelectorAll('.carousel-item.is-center');

      carouselItems.forEach((item) => {
        const title = item.querySelector('.product-title'); // Ensure your title has this class or ref

        if (title) {
          this.truncateText(title, 2);
        }
      });
    },
    truncateText(element, maxLines) {
      const lineHeight = parseInt(getComputedStyle(element).lineHeight);
      if (isNaN(lineHeight)) return;
      const maxHeight = lineHeight * maxLines;

      let truncatedText = element.innerText;
      while (element.scrollHeight > maxHeight && truncatedText.length > 0) {
        truncatedText = truncatedText.slice(0, -1);
        element.innerText = truncatedText + '...';
      }
    },
    redirectToProduct(link) {
      window.location.href = link;
    },
    isCenter(index) {
      return index === this.currentIndex;
    },
    getClass(index) {
      const total = this.immersiveProductData.products.length;
      const prevIndex = (this.currentIndex - 1 + total) % total;
      const nextIndex = (this.currentIndex + 1) % total;

      if (index === this.currentIndex) {
        return 'is-center';
      } else if (index === prevIndex) {
        return 'is-left';
      } else if (index === nextIndex) {
        return 'is-right';
      } else if (index === (this.currentIndex - 2 + total) % total) {
        return 'left-hide';
      } else if (index === (this.currentIndex + 2) % total) {
        return 'right-next';
      } else {
        return '';
      }
    },
    onButtonClick(index) {
      if (
        this.immersiveProductData &&
        this.immersiveProductData.products.length > 0 &&
        this.immersiveProductData.products[index].tagging_click
      ) {
        try {
          dataLayer.push(this.immersiveProductData.products[index].tagging_click);
        } catch {
          console.error('error in tagging');
        }
      }
    },
  },
};
</script>
